import { Provider as ReduxProvider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { TooltipProvider } from '@/components/ui/tooltip';
import { Toaster } from '@/components/ui/sonner';
import { store } from '@/store';
import ThemeProvider from './ThemeProvider';
import { Suspense, Component, ReactNode } from 'react';
import { useRoutes } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Loader2, AlertTriangle } from 'lucide-react';
import routes from '@/routes/routes';
import { AuthProvider } from "@/contexts/AuthProvider";
import { HeaderInset, HeaderProvider } from '@/components/ui/header';
import Header from '@/components/Header';
import ElementPickerProvider from './contexts/ElementPickerProvider';

function Spinner() {
  return (
    <Card className="mx-auto mt-24 w-fit p-8">
      <Loader2 className="mx-auto h-8 w-8 animate-spin" />
    </Card>
  );
}

function AppRoutes() {
  const routing = useRoutes(routes);
  return <Suspense fallback={<Spinner />}>{routing}</Suspense>;
}

// Error Boundary Component
interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error details including component stack
    console.error('Error caught by boundary:', error);
    console.error('Component stack:', errorInfo.componentStack);

    // Store error info for display
    this.setState({ errorInfo });

    if ((window as any).__Nut_onReactError__) {
      const message = error.message ?? String(error);
      console.log(
        "Calling __Nut_onReactError__",
        message,
        errorInfo.componentStack
      );
      (window as any).__Nut_onReactError__(message, errorInfo.componentStack);
    }
  }

  render() {
    if (this.state.hasError) {
      // Detect if this is a network/module loading error
      const errorMessage = this.state.error?.message || "";
      const isNetworkError =
        errorMessage.includes("Failed to fetch") ||
        errorMessage.includes("dynamically imported module") ||
        errorMessage.includes("Loading chunk") ||
        errorMessage.includes("ChunkLoadError") ||
        errorMessage.includes("NetworkError") ||
        this.state.error?.name === "ChunkLoadError";

      const title = isNetworkError
        ? "Network Connection Error"
        : "Something went wrong";
      const description = isNetworkError
        ? "We're having trouble loading the application resources. Please check your internet connection and try refreshing the page."
        : "We're sorry, but something unexpected happened. Please try refreshing the page.";

      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
          <Card className="max-w-md w-full p-8">
            <div className="flex flex-col items-center text-center space-y-4">
              <AlertTriangle className="h-12 w-12 text-destructive" />
              <h1 className="text-2xl font-bold">{title}</h1>
              <p className="text-muted-foreground">{description}</p>
              <details className="w-full text-left">
                <summary className="cursor-pointer text-sm font-medium">
                  Error details
                </summary>
                <div className="mt-2 space-y-2">
                  {this.state.error && (
                    <pre className="text-xs overflow-auto p-2 bg-muted rounded max-h-40">
                      <strong>Error:</strong>
                      {"\n"}
                      {this.state.error.toString()}
                      {"\n\n"}
                      {this.state.error.stack}
                    </pre>
                  )}
                  {this.state.errorInfo && (
                    <pre className="text-xs overflow-auto p-2 bg-muted rounded max-h-40">
                      <strong>Component Stack:</strong>
                      {this.state.errorInfo.componentStack}
                    </pre>
                  )}
                </div>
              </details>
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
              >
                Refresh Page
              </button>
            </div>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

export default function App() {
  return (
    <ErrorBoundary>
      <ReduxProvider store={store}>
        <AuthProvider>
          <ThemeProvider>
            <TooltipProvider delayDuration={200}>
              <BrowserRouter>
                <HeaderProvider defaultOpen={false}>
                  <ElementPickerProvider>
                    <div className="min-h-screen bg-background w-full">
                      <HeaderInset>
                        <Header />
                        <AppRoutes />
                      </HeaderInset>
                    </div>
                  </ElementPickerProvider>
                </HeaderProvider>
              </BrowserRouter>
              <Toaster />
            </TooltipProvider>
          </ThemeProvider>
        </AuthProvider>
      </ReduxProvider>
    </ErrorBoundary>
  );
}
